import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Autocomplete, Box, MenuItem, Tab, Tabs, TextField } from '@mui/material';
import { useAccountsBase } from './voucherShared';

// Shared pieces of the Accounts Expenses screens (operational grid + All Vouchers register).
// Filters are only sent to the server; nothing is filtered in the browser.

// Compact filter bar used on every Expense screen: small fields in an auto-filling grid,
// so a full set of filters fits on about two lines. The search box takes two cells.
export const compactFilterBarSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
  gap: 1,
  alignItems: 'start',
  mb: 2,
  '& > .MuiFormControl-root, & > .MuiAutocomplete-root': { width: '100%', minWidth: '0 !important' },
  '& > .MuiFormControl-root:has(input[placeholder^="Search"])': { gridColumn: 'span 2' },
  '& .MuiInputBase-root, & .MuiInputLabel-root, & .MuiAutocomplete-input': { fontSize: '0.8rem' },
  '& .MuiInputBase-input': { py: '6px' },
  '& > .MuiButton-root, & > .MuiChip-root': { justifySelf: 'start', alignSelf: 'center' },
};

export const CompactFilterBar = ({ children, sx, ...rest }) => (
  <Box sx={{ ...compactFilterBarSx, ...sx }} {...rest}>{children}</Box>
);

// Payment Type is a server enum (ExpenseVoucher.PAYMENT_TYPES), in erp_expense order
export const PAYMENT_TYPE_OPTIONS = [
  { value: 'ADVANCED', label: 'Advanced' },
  { value: 'SETTLEMENT', label: 'Settlement' },
  { value: 'NEW_ADVANCED', label: 'New Advanced' },
  { value: 'FINAL_PAYMENT', label: 'Final Payment' },
  { value: 'REIMBURSABLE', label: 'Reimbursable' },
  { value: 'PAID_VIA', label: 'Paid Via (card / account)' },
];

export const useDebounced = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

// Tabs at the top of Accounts Expenses: the operational payment view and the register
export const AccountsExpensesTabs = () => {
  const base = useAccountsBase();
  const { pathname } = useLocation();
  const value = pathname.startsWith(`${base}/register`) ? 'register' : 'grid';
  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
      <Tabs value={value} aria-label="Accounts Expenses views">
        <Tab value="grid" label="Payments" component={RouterLink} to={base} sx={{ textTransform: 'none' }} />
        <Tab value="register" label="All Vouchers" component={RouterLink} to={`${base}/register`} sx={{ textTransform: 'none' }} />
      </Tabs>
    </Box>
  );
};

// Employee picker (active users); the value is the user id sent as employeeId
export const EmployeeFilter = ({ users, value, onChange, sx = { minWidth: 240 } }) => (
  <Autocomplete
    size="small"
    options={users}
    value={users.find((u) => u.value === value) || null}
    onChange={(e, opt) => onChange(opt ? opt.value : '')}
    getOptionLabel={(o) => o.label || ''}
    isOptionEqualToValue={(o, v) => o.value === v.value}
    sx={sx}
    renderInput={(params) => <TextField {...params} label="Employee" />}
  />
);

export const PaymentTypeFilter = ({ value, onChange, sx = { minWidth: 170 } }) => (
  <TextField select size="small" label="Payment type" value={value} onChange={(e) => onChange(e.target.value)} sx={sx}>
    <MenuItem value="">All</MenuItem>
    {PAYMENT_TYPE_OPTIONS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
  </TextField>
);

// From / To dates (YYYY-MM-DD, both inclusive on the server)
export const DateRangeFilter = ({ label, from, to, onFromChange, onToChange, sx = { width: 170 } }) => (
  <>
    <TextField size="small" type="date" label={`${label} from`} value={from} onChange={(e) => onFromChange(e.target.value)}
      InputLabelProps={{ shrink: true }} inputProps={{ max: to || undefined, 'aria-label': `${label} from` }} sx={sx} />
    <TextField size="small" type="date" label={`${label} to`} value={to} onChange={(e) => onToChange(e.target.value)}
      InputLabelProps={{ shrink: true }} inputProps={{ min: from || undefined, 'aria-label': `${label} to` }} sx={sx} />
  </>
);
